import { Link } from "react-router-dom";

import Seo from "../../components/Seo";
import { seoFromPublicPage } from "../../seo/publicPages";
import ArticleLayout from "./ArticleLayout";
import RelatedGuides from "./RelatedGuides";

export default function NormaDeConsumPage() {
  return (
    <>
      <Seo {...seoFromPublicPage("/norma-de-consum-foaie-parcurs")} />
      <ArticleLayout
        eyebrow="Consum"
        title="Norma de consum pe foaia de parcurs: cum o stabilești și ce verifică ANAF"
        lead="Norma proprie de consum nu e cifra din cartea tehnică copiată ad litteram. E o normă a ta, realistă pentru cum circulă vehiculul, trecută pe foaie și folosită la reconcilierea cu alimentările și cu rezervorul."
      >
        <p>
          Printre cele 4 informații minime ale foii de parcurs, una e tratată adesea ca
          ornament: <strong>norma proprie de consum carburant pe kilometru</strong>.
          Categoria, locul și kilometrii se văd. Norma e un număr. Dacă e copiată din alt
          vehicul, din prospectul de showroom sau lăsată goală, la control nu are cine
          s-o apere.
        </p>
        <p>
          Legea nu-ți impune norma producătorului. Îți cere o{" "}
          <strong>normă proprie</strong>, adică asumată de PFA sau de firmă, potrivită
          condițiilor reale. De aici se leagă litrii din bonuri, rezervorul și kilometrii
          din bord. Fără ea, foaia e incompletă — chiar dacă tabelul de rute arată bine.
        </p>

        <h2>Unde e cerută, legal</h2>
        <p>
          Pentru impozit (venit net / profit), <strong>pct. 16 alin. (2)</strong> din
          normele art. 25 cere foaie cu: categorie, scop și loc, kilometri,{" "}
          <strong>norma proprie de consum</strong>. Pentru TVA,{" "}
          <strong>pct. 68 alin. (2)</strong> din normele art. 298 cere același minim. În
          practică, setul e identic. Restul (șofer, alimentări, semnătură) e recomandat,
          nu înlocuitor al normei.
        </p>
        <p>
          Nu există formular ANAF care să-ți calculeze litrii. Nici Ordinul cu model
          pentru sectorul public nu-ți fixează consumul. Tu stabilești norma. Tu o
          documentezi. Tu o folosești consecvent pe foile lunii.
        </p>

        <h2>Nu e consumul din cartea tehnică</h2>
        <p>
          Cifra WLTP sau „mixt" din talon e un <strong>punct de plecare</strong>, nu
          norma ta. Trafic urban, aer condiționat, vehicul rulat, încărcare, iarnă — toate
          mută consumul în sus. Dacă treci 4,5 l/100 km pe un autoturism care în oraș face
          7, alimentările nu vor încăpea în poveste: rezervorul „se golește" prea rar sau
          prea des față de km.
        </p>
        <p>
          Greșeala inversă e la fel de vizibilă: o normă umflată ca să „încapă" orice
          alimentare, inclusiv cele fără traseu. Inspectorul nu cere un laborator. Cere
          ca <strong>km × normă</strong> să se lege de litrii cumpărați și de capacitatea
          rezervorului, cu o marjă rezonabilă, nu cu magie.
        </p>

        <h2>Cum o stabilești, în practică</h2>

        <h3>Punctul de plecare</h3>
        <p>
          Pornește de la consumul declarat de producător (mixt sau, dacă circuli
          predominant în oraș, urban). E referința, nu valoarea finală.
        </p>

        <h3>Ajustare pentru condiții reale</h3>
        <p>
          Adaugă <strong>+10–15%</strong> pentru trafic, AC, vehicul cu km mulți, trasee
          scurte. Consumul real e aproape întotdeauna peste cel din prospect.
        </p>

        <h3>Verificare cu datele existente</h3>
        <p>
          Dacă ai deja 2–3 luni de alimentări și km de bord, calculează consumul real
          (litri / km × 100) și alege o normă aproape de acea medie, nu de prospect.
        </p>

        <h3>Documentare formală</h3>
        <p>
          Scrie norma într-o <strong>decizie internă</strong>: vehicul, număr de
          înmatriculare, valoare (l/100 km), dată, semnătură. La control, ai dovada.
        </p>

        <h3>Folosire consecventă</h3>
        <p>
          Treci aceeași valoare în antetul foii lunare. Nu o schimba de la o zi la alta
          fără motiv documentat.
        </p>

        <h3>Reverificare anuală</h3>
        <p>
          Verifică norma anual sau la schimbarea regimului de folosire (motor
          recondiționat, trecere de la extraurban la oraș, altă motorizare).
        </p>

        <p>
          Pe foaia lunară, antetul include norma alături de CUI, vehicul, perioadă și km
          de început. În sumar: total km, km final odometru, consum estimat după normă,
          combustibil rămas în rezervor. Tabelul de alimentări (dată, litri, preț, stație)
          face reconcilierea posibilă. Detaliile de structură sunt în{" "}
          <Link to="/model-foaie-parcurs">articolul despre modelul de foaie</Link>; aici
          contează că <strong>norma e aceeași sursă</strong> pentru estimarea de consum.
        </p>

        <h2>Cum se leagă de alimentări și de rezervor</h2>
        <p>
          Logica e simplă, și de aceea se vede când e ruptă: kilometrii parcurși × norma ≈
          litrii care ar fi trebuit consumați. Adaugi stocul din rezervor la început,
          scazi stocul de la final, compari cu litrii alimentați. Dacă rezervorul e de 50
          l și „consumi" 80 l între două plinuri fără km corespunzători, foaia nu ține.
          Dacă alimentezi 10 l și declari 2.000 km la 8 l/100 km, la fel.
        </p>
        <p>
          Aplicațiile de foi de parcurs fac exact această verificare: coerență{" "}
          <strong>kilometri – consum – rezervor</strong>. Nu înlocuiesc norma ta. O
          folosesc. O normă copiată din alt vehicul sau din cartea tehnică, neschimbată 5
          ani, iese la iveală când flota are motorizări diferite.
        </p>

        <h2>Greșeli frecvente</h2>

        <h3>Normă lipsă</h3>
        <p>
          Foaia are rute, dar n-are consum asumat. Minimul legal e incomplet — e ca și
          cum lipsește unul din cele 4 elemente.
        </p>

        <h3>Normă copiată</h3>
        <p>
          Copiată din alt vehicul sau dintr-un template găsit online. La control,
          inconsistența cu mașina reală iese la iveală.
        </p>

        <h3>Doar cifra producătorului</h3>
        <p>
          Norma din prospect, fără ajustare pentru oraș, iarnă, vehicul rulat. Consumul
          real e aproape întotdeauna mai mare.
        </p>

        <h3>Normă schimbată lunar</h3>
        <p>
          Ajustată de la o lună la alta ca să coincidă cu bonurile. Inspectorul vede
          tiparele.
        </p>

        <h3>Fără decizie internă</h3>
        <p>
          La control, nimeni nu poate spune cine a stabilit cifra și când. Lipsa
          documentării slăbește dosarul.
        </p>

        <h3>Alimentări care nu încap</h3>
        <p>
          Litri care depășesc capacitatea rezervorului sau care nu se potrivesc cu km,
          dar norma rămâne neatinsă. Formula simplă nu iese.
        </p>

        <div className="article-doc__callout article-doc__callout--warning">
          <strong>De evitat</strong>
          <p>
            Să „repari" consumul umflând kilometrii sau scopurile, în loc să corectezi
            norma sau evidența alimentărilor. Să ai o normă pentru 100% și o practică de
            uz mixt. Inconsistența dintre foaie, bonuri și rezervor e un motiv clasic de
            recalculare a deducerii.
          </p>
        </div>

        <h2>50% versus 100%</h2>
        <p>
          La <strong>50%</strong> foaia nu e obligatorie; norma nu e piesa centrală a
          dosarului. La <strong>100%</strong>, e unul dintre cele 4 elemente. Fără ea, nu
          ai foaie „corect întocmită", oricât de frumos e tabelul de clienți. Calculul 50%
          versus 100% e în{" "}
          <Link to="/deducere-100-combustibil">articolul de deducere</Link>. Aici: dacă
          pretinzi exclusivitate, <strong>norma trebuie să existe, să fie a ta și să se
          lege de combustibil</strong>.
        </p>

        <h2>Concluzie</h2>
        <p>
          Stabilește o cifră realistă, scrie-o într-o decizie scurtă, trece-o pe fiecare
          foaie, verifică-o cu alimentările. Nu e un exercițiu de inginerie. E al patrulea
          element fără de care foaia nu e completă.
        </p>
        <p>
          Pașii scurți pentru PFA rămân și în{" "}
          <Link to="/foi-parcurs-pfa">ghidul PFA</Link>. Articolul de față e pagina despre{" "}
          <strong>normă</strong>, valabilă și pentru SRL. Pentru trasee și casă–birou, vezi{" "}
          <Link to="/foaie-parcurs-casa-serviciu">ghidul dedicat</Link>. Pentru dreptul de
          a folosi o mașină personală, vezi{" "}
          <Link to="/foi-parcurs-comodat">comodatul</Link>.
        </p>
        <RelatedGuides currentPath="/norma-de-consum-foaie-parcurs" />
      </ArticleLayout>
    </>
  );
}
